/** Ролевая модель (ТЗ п.2): 4 операционные роли + 2 административные. */
export const ROLES = [
  'DISPATCHER',
  'MECHANIC',
  'MEDIC',
  'DRIVER',
  'DRIVER_CHIEF',
  'ADMIN',
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  DISPATCHER: 'Уполномоченное лицо / Диспетчер',
  MECHANIC: 'Механик КТП',
  MEDIC: 'Медработник',
  DRIVER: 'Водитель',
  DRIVER_CHIEF: 'Начальник водителей',
  ADMIN: 'Администратор',
};

/**
 * Разрешения по ролям — единый источник правды для guard'ов на бэке
 * и для скрытия элементов интерфейса на фронте.
 */
export const PERMISSIONS = [
  'shift:read',
  /**
   * Свой экран водителя: подтверждение назначения, получение и сдача листа, отметки по
   * выездам. Отдельно от `shift:write` намеренно — водитель правит ровно свою смену и
   * ровно эти поля, а диспетчер те же действия выполняет по праву на запись.
   */
  'shift:confirm',
  /**
   * Видеть смены и путевые листы всего парка. У водителя этого права нет:
   * он читает только свои смены, иначе получает доступ к ИИН коллег
   * и примечаниям чужих медосмотров.
   */
  'shift:read-all',
  'shift:write',
  'shift:replace-resource',
  'shift:close',
  'waybill:read',
  'waybill:sign',
  'medical:write',
  'technical:write',
  'directory:read',
  'directory:write',
  'user:manage',
  'audit:read',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  // Создание и корректировка смен, контроль готовности, подпись ЭПЛ ЭЦП ЮЛ,
  // фиксация выезда и возврата, замена ресурсов, закрытие смены.
  DISPATCHER: [
    'shift:read',
    'shift:read-all',
    'shift:write',
    'shift:replace-resource',
    'shift:close',
    'waybill:read',
    'waybill:sign',
    'directory:read',
    'audit:read',
  ],
  // Предрейсовый/послерейсовый техосмотр, одометр и топливо, QR-подпись.
  MECHANIC: ['shift:read', 'shift:read-all', 'waybill:read', 'technical:write', 'directory:read'],
  // Регистрация результатов медосмотра, QR-подпись.
  MEDIC: ['shift:read', 'shift:read-all', 'waybill:read', 'medical:write', 'directory:read'],
  // Получение смены, подтверждение/отказ, 2-кратное подтверждение ПЛ.
  DRIVER: ['shift:read', 'shift:confirm', 'waybill:read'],
  // Планирование смен, распределение водителей и техники, устранение конфликтов.
  DRIVER_CHIEF: ['shift:read', 'shift:read-all', 'shift:write', 'directory:read'],
  // Пользователи, роли, привязка номеров WhatsApp, справочники.
  ADMIN: [
    'user:manage',
    'directory:read',
    'directory:write',
    'audit:read',
    'shift:read',
    'shift:read-all',
    'waybill:read',
  ],
};

export function can(roles: readonly Role[] | undefined, permission: Permission): boolean {
  if (!roles || roles.length === 0) {
    return false;
  }
  return roles.some((role) => ROLE_PERMISSIONS[role]?.includes(permission));
}
